// TODO(APP-8243): Fix type conversions in the fragment variables project

/**
 * Runtime schemas to map untyped Structs to JS objects safely.
 *
 * The DB schemas and gRPC return types of robot configs are untyped.
 * The schemas in this module protect the UI from errors and
 * give us well defined signatures until the backend can become
 * the sole source of truth via protos or similar.
 */
import { countBy, uniqBy } from 'lodash-es';
import type { RequireExactlyOne, Simplify } from 'type-fest';
import { z, type ZodIssue } from 'zod';

import type { ResourceName } from '@viamrobotics/sdk';

import {
  createRawBillingConfigSchema,
  createRawJobConfigSchema,
  createRawModuleConfigSchema,
  createRawProcessConfigSchema,
  createRawRemoteConfigSchema,
  createRawTriggerConfigSchema,
  RawBillingConfigSchema,
  RawJobConfigSchema,
  RawModuleConfigSchema,
  RawProcessConfigSchema,
  RawRemoteConfigSchema,
  RawTriggerConfigSchema,
  RawUIFolderSchema,
} from '$lib/config-schemas';
import {
  allowVariablePlaceholders,
  variablePlaceholderSchema,
} from '$lib/config-schemas/raw-variable-placeholder';
import { AllCaptureMethodNames } from '$lib/rdk';
import { isAPITriplet, type ResourceTripletString } from '$lib/resource-type';

import {
  isCommonResource,
  type OverwriteOperator,
  type ReadonlyResource,
  SupportedOverwriteOperators,
} from './config-item';
import { createFrameSchema } from './spatial';

export type RawPartConfig = z.infer<typeof RawPartConfigSchemaBase>;

export type RawFragmentConfig = z.infer<typeof RawFragmentConfigSchema>;

export type RawFragmentImportConfig = z.infer<
  typeof RawFragmentImportConfigSchema
>;

export type RawResourceConfig = z.infer<z.AnyZodObject>;

export type RawComponentConfig = z.infer<typeof RawComponentConfigSchema>;

export type RawAssociatedServiceConfig = z.infer<
  ReturnType<typeof createRawAssociatedServiceConfigSchema>
>;

export type RawDataManagerCaptureMethodConfig = z.infer<
  ReturnType<typeof createRawDataManagerCaptureMethodSchema>
>;

export type RawServiceConfig = z.infer<typeof RawServiceConfigSchema>;

export type RawPackageConfig = z.infer<typeof RawPackageConfigSchema>;

export type RawNetworkConfig = z.infer<typeof RawNetworkConfigSchema>;

export type RawAgentConfig = z.infer<typeof RawAgentConfigSchema>;

export type RawMaintenanceConfig = z.infer<typeof RawMaintenanceConfigSchema>;

export type RawFragmentModOperation = z.infer<
  typeof RawFragmentModOperationSchema
>;

export type RawFragmentModConfig = z.infer<typeof RawFragmentModConfigSchema>;
export type RawFragmentImportConfigObject = z.infer<
  typeof RawFragmentImportConfigObjectSchema
>;

export type ModdableRawFragmentConfig = z.infer<
  typeof ModdableRawFragmentConfigSchema
>;

const UnknownRecord = z.object({}).passthrough();

const createRawDataManagerCaptureMethodSchema = (allowVariables = false) => {
  return allowVariablePlaceholders(
    z.object({
      method: z.enum(AllCaptureMethodNames),
      capture_frequency_hz: z.number(),
      disabled: z.boolean().optional(),
      additional_params: allowVariablePlaceholders(
        z.object({
          mime_type: z.string().optional(),
          camera_name: z.string().optional(),
          pin_name: z.string().optional(),
          reader_name: z.string().optional(),
          filter_source_names: z.array(z.string()).optional(),
        }),
        allowVariables
      )
        .passthrough()
        .optional(),
      recent_data_store: allowVariablePlaceholders(
        z.object({
          stored_hours: z.number().int().nonnegative().optional(),
        }),
        allowVariables
      )
        .partial()
        .passthrough()
        .optional(),
    }),
    allowVariables
  ).passthrough();
};

const createRawAssociatedServiceConfigSchema = (allowVariables = false) => {
  return allowVariablePlaceholders(
    z.object({
      type: z.string(),
      attributes: z
        .object({
          capture_methods: z
            .array(createRawDataManagerCaptureMethodSchema(allowVariables))
            .optional(),
          retention_policy: allowVariablePlaceholders(
            z.object({
              days: z.number().int().nonnegative(),
              binary_limit_gb: z.number().int().nonnegative(),
              tabular_limit_gb: z.number().int().nonnegative(),
            }),
            allowVariables
          )
            .partial()
            .passthrough()
            .optional(),
        })
        .passthrough(),
    }),
    allowVariables
  );
};

const createRawLogConfigurationSchema = (allowVariables = false) => {
  return allowVariablePlaceholders(
    z.object({
      level: z.enum(['error', 'warn', 'debug', 'info']).optional(),
    }),
    allowVariables
  ).passthrough();
};

interface MaybeResourceTypes {
  api?: ResourceTripletString | undefined;
  type?: string | undefined;
  namespace?: string | undefined;
}

const hasAPIField = <T extends MaybeResourceTypes>(
  value: T
): value is Simplify<T & RequireExactlyOne<T, 'api' | 'type'>> => {
  const fields = new Set(Object.keys(value));
  const hasAPI = fields.has('api');
  const hasType = fields.has('type');
  const hasNamespace = fields.has('namespace');

  return (hasAPI && !hasType && !hasNamespace) || (hasType && !hasAPI);
};

/**
 * Checks if a fleet resource matches a RDK resource name provided by robot APIs.
 *
 * @param resource - The fleet resource to check.
 * @param rdkResourceName - The RDK resource name to check against.
 * @returns True if the fleet resource matches the RDK resource name, false otherwise.
 */
export const resourceNameMatchesRDK = (
  resource: ReadonlyResource | undefined,
  rdkResourceName: ResourceName | undefined
) => {
  if (!rdkResourceName || !resource) {
    return false;
  }

  if (isCommonResource(resource)) {
    return (
      resource.api.namespace === rdkResourceName.namespace &&
      resource.api.type === rdkResourceName.type &&
      resource.api.subtype === rdkResourceName.subtype &&
      resource.name === rdkResourceName.name
    );
  }

  return false;
};

const createRawComponentConfigSchema = (allowVariables = false) => {
  const baseSchema = allowVariablePlaceholders(
    z.object({
      name: z.string(),
      api: z
        .string()
        .refine((value: string) => isAPITriplet(value, 'component'), {
          message:
            '`api` must match the format `{namespace}:component:{subtype}`',
        })
        .optional(),
      namespace: z.string().optional(),
      type: z.string().optional(),
      model: z.string().optional(),
      attributes: UnknownRecord.optional(),
      frame: createFrameSchema(allowVariables).optional(),
      depends_on: z
        .array(
          allowVariables ? z.string().or(variablePlaceholderSchema) : z.string()
        )
        .optional(),
      notes: z.string().optional(),
      service_configs: z
        .array(createRawAssociatedServiceConfigSchema(allowVariables))
        .optional(),
      disabled: z.boolean().optional(),
      log_configuration: createRawLogConfigurationSchema(allowVariables)
        .or(z.null())
        .optional(),
      ui_folder: RawUIFolderSchema.optional(),
    }),
    allowVariables
  );

  return baseSchema.passthrough().refine(hasAPIField, {
    message:
      'component cannot specify both `api` and `type`/`namespace`; remove `type` and `namespace` fields.',
  });
};

const RawComponentConfigSchema = createRawComponentConfigSchema(false);

// Export the factory functions for testing and fragment config
export { createRawComponentConfigSchema };

const createRawServiceConfigSchema = (allowVariables = false) => {
  const baseSchema = allowVariablePlaceholders(
    z.object({
      name: z.string(),
      api: z
        .string()
        .refine((value: string) => isAPITriplet(value, 'service'), {
          message:
            '`api` must match the format `{namespace}:service:{subtype}`',
        })
        .optional(),
      namespace: z.string().optional(),
      type: z.string().optional(),
      model: z.string().optional(),
      attributes: allowVariablePlaceholders(
        z.object({
          delete_data_on_part_deletion: z.boolean().optional(),
        }),
        allowVariables
      )
        .partial()
        .passthrough()
        .optional(),
      depends_on: z
        .array(
          allowVariables ? z.string().or(variablePlaceholderSchema) : z.string()
        )
        .optional(),
      notes: z.string().optional(),
      service_configs: z
        .array(createRawAssociatedServiceConfigSchema(allowVariables))
        .optional(),
      disabled: z.boolean().optional(),
      log_configuration: createRawLogConfigurationSchema(allowVariables)
        .or(z.null())
        .optional(),
      ui_folder: RawUIFolderSchema.optional(),
    }),
    allowVariables
  );

  return baseSchema.passthrough().refine(hasAPIField, {
    message:
      'service cannot specify both `api` and `type`/`namespace`; remove `type` and `namespace` fields.',
  });
};

const RawServiceConfigSchema = createRawServiceConfigSchema(false);

export const createRawPackageConfigSchema = (allowVariables = false) => {
  return allowVariablePlaceholders(
    z.object({
      name: z.string(),
      package: z.string(),
      version: z.string().optional(),
      type: z.string(),
      disabled: z.boolean().optional(),
      notes: z.string().optional(),
      ui_folder: RawUIFolderSchema.optional(),
    }),
    allowVariables
  ).passthrough();
};

const RawPackageConfigSchema = createRawPackageConfigSchema();

export const createRawNetworkConfigSchema = (allowVariables = false) => {
  return allowVariablePlaceholders(
    z.object({
      bind_address: z.string().optional(),
      sessions: allowVariablePlaceholders(
        z.object({
          heartbeat_window: z.string().optional(),
        }),
        allowVariables
      )
        .passthrough()
        .optional(),
    }),
    allowVariables
  ).passthrough();
};

export const RawNetworkConfigSchema = createRawNetworkConfigSchema();

export const RawAgentConfigSchema = z
  .object({
    version_control: z.object({}).passthrough(),
    advanced_settings: z.object({}).passthrough(),
    network_configuration: z.object({}).passthrough(),
    additional_networks: z.record(z.string(), z.object({}).passthrough()),
    system_configuration: z.object({}).passthrough(),
  })
  .partial()
  .passthrough();

export const createRawMaintenanceConfigSchema = (allowVariables = false) => {
  return allowVariablePlaceholders(
    z.object({
      maintenance_allowed_key: z.string(),
      sensor_name: z.string(),
    }),
    allowVariables
  )
    .partial()
    .passthrough();
};

export const RawMaintenanceConfigSchema = createRawMaintenanceConfigSchema();

const resourceNameRegex = '[a-zA-Z0-9-_]{1,59}';

const arrayResourceNameRegex =
  'components|services|modules|remotes|processes|packages|triggers|jobs';
const arrayResourceRegex = `(^(${arrayResourceNameRegex})\\.(${resourceNameRegex})\\.(?!name)${resourceNameRegex}(\\.${resourceNameRegex})*)`;

const singletonResourceRegex = 'network|agent|maintenance';
const resourceRegex = `(^(${singletonResourceRegex})((\\.${resourceNameRegex})*))`;

const fieldPathRegex = new RegExp(
  `${arrayResourceRegex}|${resourceRegex}`,
  'u'
);

/**
 * Sections a fragment mod field path may target: array resources addressed as
 * `<section>.<resource name>.<field>`, and singletons addressed as `<section>[.<field>]`. Exported so
 * the assistant's edit_fragment_mods tool validates paths against the same source of truth as
 * RawFragmentModOperationSchema instead of its own copy.
 */
export const FragmentModArraySections = arrayResourceNameRegex.split(
  '|'
) as readonly string[];
export const FragmentModSingletonSections = singletonResourceRegex.split(
  '|'
) as readonly string[];
export const FragmentModFieldPathRegex = fieldPathRegex;

// TODO(mc, 2024-07-08): enforce single key in zod schema
const RawFragmentModOperationSchema = z.record(
  z.union(
    SupportedOverwriteOperators.map((operator) => z.literal(operator)) as [
      z.ZodLiteral<OverwriteOperator>,
      z.ZodLiteral<OverwriteOperator>,
      ...z.ZodLiteral<OverwriteOperator>[],
    ],
    {
      errorMap: () => ({
        message: 'Invalid operator: Expected $set or $unset',
      }),
    }
  ),
  z.record(
    z.string().regex(fieldPathRegex, {
      message:
        'Invalid field path: Can only overwrite fields within an existing resource, and cannot overwrite a fragment resource name',
    }),
    z.unknown()
  )
);

const RawFragmentModConfigSchema = z
  .object({
    fragment_id: z.string(),
    mods: z.array(RawFragmentModOperationSchema),
    prefix: z.string().optional(),
  })
  .passthrough();

const JSONValue: z.ZodType<unknown> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JSONValue),
    z.record(JSONValue),
  ])
);

const createRawFragmentImportConfigObjectSchema = (allowVariables = false) => {
  return allowVariablePlaceholders(
    z.object({
      id: z.string(),
      version: z.string().optional(),
      variables: z.record(z.unknown()).optional(),
      ui_folder: RawUIFolderSchema.optional(),
      prefix: z.string().optional(),
      disabled: z.boolean().optional(),
    }),
    allowVariables
  );
};

const createRawFragmentImportConfigSchema = (allowVariables = false) => {
  return z.union([
    z.string(),
    createRawFragmentImportConfigObjectSchema(allowVariables),
  ]);
};

export const RawFragmentImportConfigObjectSchema = z.object({
  id: z.string(),
  version: z.string().optional(),
  variables: z.record(z.unknown()).optional(),
  ui_folder: RawUIFolderSchema.optional(),
  prefix: z.string().optional(),
  disabled: z.boolean().optional(),
});

const RawFragmentImportConfigSchema = z.union([
  z.string(),
  RawFragmentImportConfigObjectSchema,
]);

const RawPartConfigSchemaBase = allowVariablePlaceholders(
  z.object({
    agent: RawAgentConfigSchema.optional(),
    billing: RawBillingConfigSchema.optional(),
    network: RawNetworkConfigSchema.optional(),
    maintenance: RawMaintenanceConfigSchema.optional(),
    components: RawComponentConfigSchema.array().optional(),
    services: RawServiceConfigSchema.array().optional(),
    modules: RawModuleConfigSchema.array().optional(),
    remotes: RawRemoteConfigSchema.array().optional(),
    processes: RawProcessConfigSchema.array().optional(),
    packages: RawPackageConfigSchema.array().optional(),
    fragments: RawFragmentImportConfigSchema.array().optional(),
    triggers: RawTriggerConfigSchema.array().optional(),
    jobs: RawJobConfigSchema.array().optional(),
    fragment_mods: RawFragmentModConfigSchema.array().optional(),
  }),
  false, // Do NOT allow variables for part config
  [] // No exclusions needed since allowVariables is false
).passthrough();

export const RawPartConfigSchema = RawPartConfigSchemaBase.superRefine(
  (value, ctx) => {
    const nameValidationIssues = validateUniqueResourceNames(value);
    for (const issue of nameValidationIssues) {
      ctx.addIssue(issue);
    }
  }
);

/**
 * Every top-level section key of a raw part config, derived from the schema. The assistant's
 * update_config_item section table (update-config-item.ts) is checked against this by a test, so a
 * config section added here can't silently become uneditable there.
 */
export const RawPartConfigSectionKeys = RawPartConfigSchemaBase.keyof().options;
export type RawPartConfigSectionKey = (typeof RawPartConfigSectionKeys)[number];

/**
 * Helper function to extract string names from an array of resources that have a 'name' property.
 * Filters out non-string names to ensure type safety.
 */
const extractStringNames = (
  resources: { name?: unknown }[] | undefined
): string[] => {
  return (
    resources
      ?.map((resource) => resource.name)
      .filter(
        (resourceName): resourceName is string =>
          typeof resourceName === 'string'
      ) ?? []
  );
};

/**
 * The sections whose identity values (name, or id for processes) share one uniqueness pool. Single
 * source of truth for that pool: validateUniqueResourceNames enforces it, duplicateResourceNameIssues
 * recomputes it on a raw object, and the assistant's update_config_item rename guard imports it.
 */
export const UNIQUE_NAME_POOL_SECTION_KEYS = [
  'components',
  'services',
  'modules',
  'remotes',
  'processes',
  'packages',
  'triggers',
  'jobs',
] as const;

// Each pooled section (UNIQUE_NAME_POOL_SECTION_KEYS) needs bespoke identity handling here —
// modules dedup by name, processes are keyed by id — so the fields are destructured explicitly.
const validateUniqueResourceNames = (
  rawPartConfig: RawPartConfig
): ZodIssue[] => {
  const {
    components,
    services,
    modules,
    remotes,
    packages,
    triggers,
    jobs,
    processes,
  } = rawPartConfig;
  const dedupedModules = uniqBy(modules, (module) => module.name);
  const names = [
    ...extractStringNames(components),
    ...extractStringNames(services),
    ...extractStringNames(dedupedModules),
    ...extractStringNames(remotes),
    ...extractStringNames(packages),
    ...extractStringNames(triggers),
    ...extractStringNames(jobs),
    ...(processes?.map(({ id }) => id) ?? []),
  ];
  const nameCounts = countBy(names);
  return Object.entries(nameCounts)
    .filter(([, count]) => count > 1)
    .map(([name]) => ({
      code: z.ZodIssueCode.custom,
      path: [],
      message: `Multiple resources use name ${name}. Resource names must be unique.`,
    }));
};

// zod v3 skips superRefine when the structural parse aborts, so safeParse hides these issues on
// a broken config; before/after issue diffing computes them directly on the raw object instead.
export const duplicateResourceNameIssues = (
  raw: Record<string, unknown>
): ZodIssue[] =>
  validateUniqueResourceNames(
    Object.fromEntries(
      UNIQUE_NAME_POOL_SECTION_KEYS.map((key) => {
        const section = raw[key];
        return [
          key,
          Array.isArray(section)
            ? section.filter(
                (element) => element !== null && element !== undefined
              )
            : undefined,
        ];
      })
    )
  );

/**
 * Raw fragment configuration.
 *
 * Not a passthrough, because we do not need to write this configuration (yet).
 */
export const RawFragmentConfigSchema = allowVariablePlaceholders(
  z.object({
    description: z.string().optional(),
    agent: RawAgentConfigSchema,
    billing: createRawBillingConfigSchema(true).optional(),
    network: createRawNetworkConfigSchema(true).optional(),
    maintenance: createRawMaintenanceConfigSchema(true).optional(),
    components: createRawComponentConfigSchema(true).array(),
    services: createRawServiceConfigSchema(true).array(),
    modules: createRawModuleConfigSchema(true).array(),
    remotes: createRawRemoteConfigSchema(true).array(),
    processes: createRawProcessConfigSchema(true).array(),
    packages: createRawPackageConfigSchema(true).array(),
    triggers: createRawTriggerConfigSchema(true).array(),
    jobs: createRawJobConfigSchema(true).array(),
    fragments: createRawFragmentImportConfigSchema(true).array(),
  }),
  true // Allow variables for fragment config at all levels
)
  .partial()
  .passthrough();

const MappedResourcesSchema = z.object({
  components: z.record(RawComponentConfigSchema),
  services: z.record(RawServiceConfigSchema),
  modules: z.record(z.array(RawModuleConfigSchema)),
  remotes: z.record(RawRemoteConfigSchema),
  processes: z.record(RawProcessConfigSchema),
  packages: z.record(RawPackageConfigSchema),
  triggers: z.record(RawTriggerConfigSchema),
  jobs: z.record(RawJobConfigSchema),
});

/**
 * Schema for a raw fragment that is ready to have fragment overwrites applied.
 *
 * Top-level resource lists from a raw fragment config are transformed from lists
 * to resource name-keyed maps to allow for name-based overwrites
 * (e.g. fieldpath `components.<component-name>.`)
 */
export const ModdableRawFragmentConfigSchema = RawFragmentConfigSchema.merge(
  MappedResourcesSchema
);

/**
 * A list of resource types that should be converted from lists to maps when
 * converting from a raw fragment config to a moddable fragment config.
 */
export const MappedResourceTypes = MappedResourcesSchema.keyof().options;

/**
 * A list of resource types that should not be converted from lists to maps when
 * converting from a raw fragment config to a moddable fragment config
 * (all resource types excluding mapped resource types).
 */
export const NonMappedResourceTypes =
  ModdableRawFragmentConfigSchema.keyof().exclude(MappedResourceTypes).options;
